import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';

@Component({
    selector: 'st-topic-type',
    templateUrl: './topic-type.html',
    styleUrls: ['./topic-type.scss'],
    host: {
        class: 'st-topic-type',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopicTypeComponent extends EnpsControlComponent<Array<string>> {
    public readonly noTypeItem: string = 'PSTWebListValue_PSTTopicType_NoType';
    itemList = input<Array<{ id: string; title: string }>>([]);
    record = input<StorytellingRecord>(null);
    showAdd = input(false, {
        transform: coerceBooleanProperty,
    });
    maxValuesToShow = input(2);
    showRemove = input(false, {
        transform: coerceBooleanProperty,
    });

    menuTrigger = viewChild(MatMenuTrigger);
    selectionList = viewChild(MatSelectionList);
    addTypeChip = viewChild(MatChip);

    internalValue = signal<Array<string>>([]);
    isListOpen = signal(false);

    overlay = viewChild(CdkConnectedOverlay);

    maxValuesToShowLabel = computed(() => {
        return `+ ${this.internalValue()?.length - this.maxValuesToShow()} more`;
    });

    itemsToShow = computed(() => {
        return this.internalValue()?.slice(0, this.maxValuesToShow()) || [];
    });

    showMaxValuesToShowLabel = computed(() => {
        return this.maxValuesToShow() > 0 && this.value()?.length > this.maxValuesToShow();
    });

    constructor() {
        super();
        effect(
            () => {
                this._syncValueIfNecessary(this.value());
                this.internalValue.set(this.value());
            },
            { allowSignalWrites: true }
        );
    }

    public onChange(event) {
        this.changed.emit(event);
    }

    public getItemLabel(item: any) {
        if (item === this.noTypeItem) {
            return this.appService.languageSettings.get(item);
        }
        return this.itemList().find(option => option.id.toLowerCase() === item?.toLowerCase())?.title;
    }

    internalValueChanged(event: MatSelectionListChange) {
        this.isValueChanged.set(true);
        this.internalValue.set(event.source.selectedOptions.selected.map(item => item.value));
    }

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue());
        }
    }

    removeItem(item: string) {
        this.internalValue.set(this.internalValue().filter(value => value !== item));
        this.changed.emit(this.internalValue());
    }

    extraItemsCount = computed(() => this.internalValue().length - this.maxValuesToShow());
    shouldShowExtraItems = computed(() => this.extraItemsCount() > 0);

    indexes = computed(() => {
        const length = this.shouldShowExtraItems() ? this.maxValuesToShow() : this.internalValue().length;
        return Array.from({ length }, (_, i) => i);
    });

    showStaffTooltip() {
        this.isListOpen.set(true);
    }

    onMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-staff-id-list-tooltip-content')) {
            this.isListOpen.set(false);
        }
    }

    tooltipItems = computed(() => {
        const items = this.internalValue()?.slice(this.indexes().length);
        if (items.length > 0) {
            return this.itemList()
                .filter(option => items.includes(option.id))
                .map(item => item);
        }
    });

    panelAttached() {
        const overlayElement = this.overlay()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isListOpen.set(false);
            });
        }
    }

    openMatMenu() {
        if (!this.menuTrigger().menuOpen) {
            this.menuTrigger().openMenu();
            this.selectionList()?.options?.first?.focus();
        }
    }

    onTabOrEscPress() {
        this.addTypeChip().focus();
    }
}
